import PublicPageTemplate from '../../components/PublicPageTemplate/PublicPageTemplate';
import { useFormConfig } from '../../hooks/useFormConfig';

function PsfaPrivacyNoticePage() {
  const { formConfig } = useFormConfig();
  const serviceName = (formConfig as any)?.serviceName || 'GRID GovReturns';

  return (
    <PublicPageTemplate title={`Privacy notice for ${serviceName}`} fullWidth>
      <p className="govuk-body">Last updated: 11 February 2026</p>

      <p className="govuk-body">
        This notice explains how Cabinet Office Digital, GRID GovReturns Team processes personal
        information in {serviceName}. It aligns to the Cabinet Office Personal Information Charter.
      </p>

      <h2 className="govuk-heading-m">Who is responsible for your data</h2>
      <p className="govuk-body">
        The Cabinet Office is the data controller for personal information processed through this
        service.
      </p>

      <h2 className="govuk-heading-m">What information we collect</h2>
      <p className="govuk-body">We may collect and process:</p>
      <ul className="govuk-list govuk-list--bullet">
        <li>account and sign-in data, such as email address and user identifier</li>
        <li>form responses and attachments entered into the service</li>
        <li>access and sharing permissions for users working on a form</li>
        <li>operational audit data, such as timestamps and activity history</li>
      </ul>
      <p className="govuk-body">
        Some forms may include special category information. Where this happens, we process that
        information only where a lawful UK GDPR and Data Protection Act 2018 condition applies.
      </p>

      <h2 className="govuk-heading-m">Why we use your information</h2>
      <p className="govuk-body">We use your information to:</p>
      <ul className="govuk-list govuk-list--bullet">
        <li>provide and operate the service</li>
        <li>enable submission, review, approval and reporting workflows</li>
        <li>contact users about access, security, approval and form status</li>
        <li>maintain service security, performance and auditability</li>
      </ul>

      <h2 className="govuk-heading-m">Our lawful basis</h2>
      <p className="govuk-body">
        We process personal information to perform tasks carried out in the public interest and in
        the exercise of official authority. Where required, we also rely on other lawful bases and
        additional conditions set by data protection law.
      </p>

      <h2 className="govuk-heading-m">Who we share information with</h2>
      <p className="govuk-body">
        We share information only where necessary. This includes authorised members of Cabinet
        Office Digital, GRID GovReturns Team and approved service providers that support delivery
        of this service.
      </p>
      <p className="govuk-body">
        GRID GovReturns is hosted on Amazon Web Services (AWS) in the eu-west-2 region (London,
        United Kingdom) only. We use GOV.UK Notify to send notification emails.
      </p>
      <p className="govuk-body">
        Information may be shared with the team that manages the webform service, including form
        completion progress updates and copies of submitted forms, where this is required to run,
        support and assure the service.
      </p>
      <p className="govuk-body">
        We also keep access and permission change audit logs. These logs may be reviewed for
        security, fraud prevention, compliance and incident investigation purposes.
      </p>

      <h2 className="govuk-heading-m">How long we keep your information</h2>
      <p className="govuk-body">
        We retain data for 2 years from the date of submission.
      </p>

      <h2 className="govuk-heading-m">International transfers</h2>
      <p className="govuk-body">
        All data on GRID GovReturns is stored and processed within the UK.
      </p>
      <p className="govuk-body">
        Where teams that own downstream returns process data from GRID GovReturns using Cabinet
        Office Google IT, appropriate safeguards required by UK data protection law are applied.
      </p>

      <h2 className="govuk-heading-m">Your rights</h2>
      <p className="govuk-body">You have rights under UK GDPR, including the right to:</p>
      <ul className="govuk-list govuk-list--bullet">
        <li>request access to your personal information</li>
        <li>request correction of inaccurate information</li>
        <li>request restriction or object to some processing</li>
        <li>request erasure where applicable</li>
      </ul>
      <p className="govuk-body">
        Read the{' '}
        <a
          className="govuk-link"
          href="https://www.gov.uk/government/organisations/cabinet-office/about/personal-information-charter"
          target="_blank"
          rel="noreferrer"
        >
          Cabinet Office Personal Information Charter
        </a>{' '}
        for how to exercise your rights and how the Cabinet Office handles personal information.
      </p>

      <h2 className="govuk-heading-m">Complaints</h2>
      <p className="govuk-body">
        If you are unhappy with how your personal information has been handled, you can complain to
        the Information Commissioner&apos;s Office (ICO):
      </p>
      <p className="govuk-body">
        <a
          className="govuk-link"
          href="https://ico.org.uk/make-a-complaint/"
          target="_blank"
          rel="noreferrer"
        >
          Make a complaint to the ICO
        </a>
      </p>

      <h2 className="govuk-heading-m">Contact</h2>
      <p className="govuk-body">
        For privacy questions about this service, contact Cabinet Office Digital, GRID GovReturns
        Team at{' '}
        <a className="govuk-link" href="mailto:grid-platform@cabinetoffice.gov.uk">
          grid-platform@cabinetoffice.gov.uk
        </a>
        .
      </p>
    </PublicPageTemplate>
  );
}

export default PsfaPrivacyNoticePage;
